import { AlertRuleV9RequestSubType } from '@/api-client/metav8Over/model/alert-rule-v9-request-sub-type';
import { CHECK_BY_COMBINATION } from '@/alert/utils/define';
import type { AlertMetricSubType } from '@/alert/utils/alertMetricSubType.utils';
import { lowerCase } from 'lodash-es';
import { AlertSubType, RuleCriteria } from './alertDetail.types';
import { ALERT_GRID_COLUMN_TYPE } from './alertDetail.define';

/** makeAlertTreeRows 결과 그리드 행 — 필드 구성은 그리드 컬럼에 따라 동적 */
export type AlertDetailTreeGridRow = {
  subType?: string;
  children?: Record<string, unknown>[];
  [field: string]: unknown;
};

/**
 * 알림 상세(History/Current) 트리 그리드의 root/자식 행 비움 처리 (Figma 스펙:
 * root 행은 Alert Value 비움, 자식 행은 Alert Stat Name·Alert Value만 표시).
 * `metricSubType`이 `CUSTOM`/`COMPOUND`일 때만 적용한다.
 * 자식 행 비움 필드는 History/Current 트리 그리드 컬럼의 합집합 — 탭에 없는 필드는 무시된다.
 *
 * @param rawRows makeAlertTreeRows 결과 행 배열
 * @returns 적용 시 자식 보유 root 행의 `value`와 자식 행 표시 필드를 비운 새 배열,
 *          미적용(metricSubType이 CUSTOM/COMPOUND가 아님) 시 `rawRows` 원본 참조 그대로 반환
 */
export const blankAlertDetailTreeGridRows = <T extends AlertDetailTreeGridRow>(
  rawRows: T[],
  metricSubType?: AlertMetricSubType,
): T[] => {
  const isCustomOrCompound =
    metricSubType === AlertRuleV9RequestSubType.CUSTOM ||
    metricSubType === AlertRuleV9RequestSubType.COMPOUND;
  if (!isCustomOrCompound) {
    return rawRows;
  }
  return rawRows.map((row) => {
    const children = row.children ?? [];
    return {
      ...row,
      ...(children.length ? { value: null } : {}),
      children: children.map((child) => ({
        ...child,
        alertLevel: null,
        targets: [],
        targetAlias: null,
        subName: null,
        firstTriggerTime: null,
        triggerTime: null,
        duration: null,
        notificationStatus: null,
        detail: null,
      })),
    };
  });
};

export const getGridColumnType = ({
  subType,
  ruleCriteria,
  metricConditionSubType,
}: {
  subType: AlertSubType;
  ruleCriteria: RuleCriteria;
  metricConditionSubType?: AlertMetricSubType;
}): (typeof ALERT_GRID_COLUMN_TYPE)[keyof typeof ALERT_GRID_COLUMN_TYPE] => {
  const lowerSubType = lowerCase(subType);

  if (['prometheus', 'elasticsearch'].includes(lowerSubType)) {
    return ALERT_GRID_COLUMN_TYPE.EXTERNAL;
  }

  if (lowerSubType === 'table') {
    return ALERT_GRID_COLUMN_TYPE.TABLE;
  }

  if (
    ruleCriteria === CHECK_BY_COMBINATION ||
    metricConditionSubType === AlertRuleV9RequestSubType.COMPOUND ||
    metricConditionSubType === AlertRuleV9RequestSubType.CUSTOM
  ) {
    return ALERT_GRID_COLUMN_TYPE.METRIC_COMBINATION;
  }

  if (lowerSubType === 'system' || lowerSubType === 'event statistics') {
    return ALERT_GRID_COLUMN_TYPE['SYSTEM|EVENT_STATISTICS'];
  }

  return ALERT_GRID_COLUMN_TYPE.ETC;
};
